import {
  DialogActions,
  DialogTitle,
  Dialog as MuiDialog,
  DialogContent as MuiDialogContent,
} from "@mui/material";
import { Alert, Button } from "@platform/design-system";
import isEqual from "lodash/isEqual";
import { useState } from "react";

import { useRfpStrategicCarriers } from "../../hooks/useRfpStrategicCarriers";
import { useUpdateRfp } from "../../hooks/useUpdateRfp";
import { CircularProgressCentered } from "../CircularProgressCentered";

import { DialogContent } from "./dialogContent";

type Props = {
  onClose: () => void;
  carriers: string[] | null;
  rfpId: string;
  onUpdateCarriers: (carriers: string[]) => void;
};

function Dialog({
  rfpId,
  onClose,
  onUpdateCarriers,
  carriers,
}: Props): React.JSX.Element {
  const [editingCarriers, setEditingCarriers] = useState(carriers ?? []);
  const query = useRfpStrategicCarriers({ rfpId });
  const mutation = useUpdateRfp({
    rfpId,
    onSuccess: (_, { carriers }) => {
      onUpdateCarriers(carriers);
      onClose();
    },
  });

  function handleClose(): void {
    if (mutation.isPending) {
      return;
    }
    onClose();
  }
  function handleDelete(carrier: string): void {
    setEditingCarriers(editingCarriers.filter((c) => c !== carrier));
  }
  function handleSelect(carrier: string): void {
    setEditingCarriers((cs) => [...cs, carrier]);
  }

  return (
    <MuiDialog
      open
      fullWidth
      maxWidth="md"
      onClose={handleClose}
      aria-labelledby="selected-carriers-dialog"
      slotProps={{
        paper: {
          component: "form",
          onSubmit: (event) => {
            event.preventDefault();
            if (isEqual(editingCarriers, carriers)) {
              return onClose();
            }
            mutation.mutate({ carriers: editingCarriers });
          },
        },
      }}
    >
      <DialogTitle id="selected-carriers-dialog">
        Edit Carriers To Quote
      </DialogTitle>
      <MuiDialogContent>
        {query.isLoading ? (
          <CircularProgressCentered />
        ) : query.isError ? (
          <Alert severity="error">{query.error.message}</Alert>
        ) : (
          <DialogContent
            carriers={editingCarriers}
            strategicCarriers={query.data.strategicCarriers}
            allCarriers={query.data.carriers}
            onDelete={handleDelete}
            onSelect={handleSelect}
          />
        )}
      </MuiDialogContent>
      <DialogActions>
        <Button
          variant="tertiary"
          onClick={handleClose}
          disabled={mutation.isPending}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          type="submit"
          disabled={
            editingCarriers.length === 0 || editingCarriers === carriers
          }
          loading={mutation.isPending}
        >
          Save Carriers
        </Button>
      </DialogActions>
    </MuiDialog>
  );
}

export { Dialog };
